import { useEffect, useState } from 'react'
import { Kopfleiste, Seitenkopf } from './Kopfleiste.js'
import { Fuss } from './Fuss.js'
import { useSeitenleben } from './bewegung.js'
import {
  PAKETE,
  preisText,
  merkmaleMitZahlen,
  STEUERHINWEIS,
  TESTPHASE_TAGE
} from '@raporto/shared'
import { VERGLEICH, UNTERSCHIEDE, FRAGEN } from '../pakete.js'

/**
 * Die Preisseite.
 *
 * Sie hat eine Aufgabe: jede Frage beantworten, die jemand vor dem Kauf hat,
 * damit er sie nicht stellen muss. Deshalb steht hier nirgends «auf Anfrage»
 * und nirgends «kontaktieren Sie uns» – wer beides liest, klickt weg.
 *
 * Die Tabelle zeigt bewusst auch, was in allen drei Paketen gleich ist. Ein
 * Vergleich, der nur die Unterschiede zeigt, liest sich wie eine Liste von
 * Dingen, die im kleinen Paket fehlen.
 *
 * Zahlen und Texte kommen aus pakete.ts. Diese Datei rechnet nichts und
 * behauptet nichts – sie stellt dar.
 */
export function Preise({
  angemeldet,
  aufAnmelden,
  aufStarten,
  aufStart,
  aufPfad,
  aufBestellen
}: {
  angemeldet: boolean
  aufAnmelden: () => void
  aufStarten: () => void
  aufStart: () => void
  aufPfad: (pfad: string) => void
  /** Ein Paket bestellen – der Knopf auf jeder Karte. */
  aufBestellen: (paket: string) => void
  /** AGB, Datenschutz, Auftragsbearbeitung, Impressum. */
}): JSX.Element {
  const weiter = aufAnmelden
  /*
    Der Knopf auf der Karte führt zur Bestellung, nicht zur Anmeldung.

    Vorher landeten beide beim Anmeldeformular – und ein Besucher, der auf
    «Jetzt starten» drückt und nach einem Passwort gefragt wird, das er nie
    vergeben hat, ist weg. Wer angemeldet ist, hat schon einen Betrieb; für
    ihn bleibt der Weg in die Anwendung.
  */
  const bestellen = (paket: string): void => (angemeldet ? aufStarten() : aufBestellen(paket))
  const wort = angemeldet ? 'Zur App' : 'Anmelden'

  const [geschoben, setGeschoben] = useState(false)
  useEffect(() => {
    const beiRollen = (): void => setGeschoben(window.scrollY > 8)
    beiRollen()
    window.addEventListener('scroll', beiRollen, { passive: true })
    return () => window.removeEventListener('scroll', beiRollen)
  }, [])

  /*
    Hier lag der Paketfinder: vier Fragen über den eigenen Betrieb, darunter
    ein Ergebnis. Er ist raus, und zwar nicht wegen eines Fehlers, sondern
    wegen der Länge – die Preisseite war auf dem Handy 4556 Pixel hoch, und
    der Finder beantwortete eine Frage, die der Vergleich zwei Bildschirme
    tiefer ohnehin beantwortet. Zwei Wege zur selben Antwort sind einer zu
    viel.
  */

  /*
    Die Tabelle beginnt bei den Unterschieden.

    Hier stand einmal das Gegenteil: alles zeigen, weil ein Vergleich, der nur
    die Unterschiede zeigt, sich wie eine Mängelliste des kleinen Pakets liest.
    Das Argument stimmt weiter – nur ist es seit dem Finder nicht mehr die
    erste Frage. Wer wissen will, was ihn vom nächsten Paket trennt, sieht es
    jetzt in sieben Zeilen statt in einundzwanzig; und was überall gleich ist,
    ist einen Klick entfernt und heisst auch so.
  */
  const [alleZeilen, setAlleZeilen] = useState(false)
  const zeilen = alleZeilen ? VERGLEICH : UNTERSCHIEDE

  /* Die Gruppen in der Reihenfolge ihres ersten Auftretens, ohne Doppelte. */
  const gruppen = [...new Set(zeilen.map((z) => z.gruppe))]

  /* Nur eine Antwort steht offen. Acht offene Antworten sind keine Liste mehr. */
  const [offeneFrage, setOffeneFrage] = useState<string | null>(FRAGEN[0]?.frage ?? null)

  /*
    Monatlich oder jährlich.

    Die Rechnung ist so einfach, dass sie auf der Karte stehen kann: zehn
    Monate statt zwölf. Deshalb steht dort «zwei Monate geschenkt» und nicht
    «bis zu 20 % sparen» – ein Prozentsatz zwingt zum Nachrechnen, zwei Monate
    versteht man sofort.

    Der Monatsbetrag im Jahresmodell ist der GERECHNETE Zwölftel, nicht der
    Jahrespreis. Wer die zwei Zahlen nebeneinander sieht, soll sie vergleichen
    können, ohne selbst zu teilen.
  */
  const [takt, setTakt] = useState<'monat' | 'jahr'>('monat')
  const jahrRp = (p: { preisRp: number }): number => p.preisRp * 10
  const angezeigtRp = (p: { preisRp: number }): number =>
    takt === 'jahr' ? Math.round((p.preisRp * 10) / 12) : p.preisRp

  const leben = useSeitenleben<HTMLDivElement>()

  return (
    <div ref={leben} className="rp-public rp-glasseite">
      <Kopfleiste
        hier="preise"
        angemeldet={angemeldet}
        aufStart={aufStart}
        aufPfad={aufPfad}
        aufAnmelden={aufAnmelden}
        aufStarten={aufStarten}
      />

      <Seitenkopf
        augenbraue="Pakete und Preise"
        titel="Was Raporto kostet."
        hell="Vollständig, auf einer Seite."
        meta="Schweizer Software · Datenbank in Zürich"
        vorspann="Drei Pakete, alle Beträge pro Monat und ohne Mehrwertsteuer. Keine Mindestlaufzeit, keine Einrichtungsgebühr, kein Aufpreis für den Support."
      />

      <section className="rp-preis-kopf">
        <div className="rp-mitte">
          <div className="rp-testphase rp-herein rp-v2">
            <p className="rp-testphase-titel">{TESTPHASE_TAGE} Tage kostenlos testen.</p>
            <p className="rp-testphase-text">
              Wähle ein Paket, hinterlege deine Zahlungsart und teste Raporto {TESTPHASE_TAGE} Tage
              kostenlos. Die Abrechnung beginnt erst nach Ablauf der Testphase.
            </p>
            <ul className="rp-testphase-punkte">
              <li>{TESTPHASE_TAGE} Tage kostenlos testen</li>
              <li>Ohne Mindestlaufzeit</li>
              <li>Ohne Einrichtungsgebühr</li>
              <li>Zahlungsart erforderlich</li>
            </ul>
          </div>

          <div className="rp-takt rp-herein rp-v2" role="group" aria-label="Abrechnung">
            <button
              type="button"
              id="btn-takt-monat"
              data-an={takt === 'monat' ? '' : undefined}
              onClick={() => setTakt('monat')}
            >
              Monatlich
            </button>
            <button
              type="button"
              id="btn-takt-jahr"
              data-an={takt === 'jahr' ? '' : undefined}
              onClick={() => setTakt('jahr')}
            >
              Jährlich <em>−2 Monate</em>
            </button>
          </div>
        </div>
      </section>

      {/* ================= Die drei Pakete ================= */}

      <section className="rp-abschnitt rp-preis-karten">
        <div className="rp-mitte">
          <div className="rp-pakete gross" id="paketliste">
            {PAKETE.map((p) => (
              <article
                className={p.empfohlen ? 'rp-paket empfohlen' : 'rp-paket'}
                key={p.id}
                id={`paket-${p.id}`}
              >
                {p.empfohlen && <span className="rp-paket-fahne">Beliebteste Wahl</span>}
                <h3>{p.name}</h3>
                <p className="rp-paket-wen">{p.fuerWen}</p>
                <p className="rp-paket-preis">
                  <span className="rp-paket-waehrung">CHF</span>
                  <b id={`preis-${p.id}`}>{preisText(angezeigtRp(p))}</b>
                  <span className="rp-paket-takt">/ Monat</span>
                </p>
                <p className="rp-paket-steuer">{STEUERHINWEIS}</p>
                <p className="rp-paket-jahr">
                  {takt === 'jahr'
                    ? `CHF ${preisText(jahrRp(p))} im Jahr, zwei Monate geschenkt`
                    : `oder CHF ${preisText(jahrRp(p))} im Jahr`}
                </p>
                {/*
                  Der Knopf steht VOR der Merkmalliste.

                  Wer sich beim Preis entschieden hat, soll nicht an sechs
                  Zeilen vorbeiscrollen, um ihn zu finden. Wer noch liest,
                  scrollt ohnehin weiter – für den ist die Liste darunter der
                  nächste Schritt und nicht ein Umweg.
                */}
                <button
                  className={p.empfohlen ? 'rp-btn rp-btn-primary' : 'rp-btn'}
                  id={`btn-paket-${p.id}`}
                  onClick={() => bestellen(p.id)}
                >
                  {angemeldet ? 'Zur App' : 'Paket wählen'}
                </button>
                <ul className="rp-paket-liste">
                  {merkmaleMitZahlen(p).map((m) => (
                    <li key={m}>
                      <span aria-hidden="true">✓</span> {m}
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ================= Vergleich ================= */}

      <section className="rp-abschnitt linie" id="vergleich">
        <div className="rp-mitte">
          <p className="rp-augenbraue">Vergleich</p>
          <h2>{alleZeilen ? 'Zeile für Zeile, ohne Kleingedrucktes.' : 'Wo sie sich trennen.'}</h2>
          <p className="rp-lead">
            {alleZeilen
              ? 'Auch das, was überall gleich ist. Du sollst nicht raten müssen, ob eine Grundfunktion im kleinen Paket fehlt.'
              : `Die ${UNTERSCHIEDE.length} Zeilen, in denen sich die Pakete tatsächlich unterscheiden. Der Rest ist überall gleich.`}
          </p>

          <div className="rp-vergleich-rahmen">
            <table className="rp-vergleich">
              <thead>
                <tr>
                  <th>Leistung</th>
                  {PAKETE.map((p) => (
                    <th key={p.id} className={p.empfohlen ? 'hervor' : undefined}>
                      {p.name}
                      <i>CHF {preisText(angezeigtRp(p))} / Mt.</i>
                    </th>
                  ))}
                </tr>
              </thead>
              {gruppen.map((g) => (
                <tbody key={g}>
                  <tr className="rp-vergleich-gruppe">
                    <th colSpan={4}>{g}</th>
                  </tr>
                  {zeilen
                    .filter((z) => z.gruppe === g)
                    .map((z) => (
                      <tr key={z.merkmal}>
                        <td>{z.merkmal}</td>
                        <td>{z.starter}</td>
                        <td className="hervor">{z.pro}</td>
                        <td>{z.team}</td>
                      </tr>
                    ))}
                </tbody>
              ))}
            </table>
          </div>

          <button
            type="button"
            className="rp-mehrzeilen"
            id="btn-alle-zeilen"
            aria-expanded={alleZeilen}
            onClick={() => setAlleZeilen((v) => !v)}
          >
            {alleZeilen
              ? 'Nur die Unterschiede zeigen'
              : `Alle ${VERGLEICH.length} Zeilen zeigen`}
          </button>
        </div>
      </section>

      {/* ================= Fragen ================= */}

      <section className="rp-abschnitt linie" id="fragen">
        <div className="rp-mitte">
          <p className="rp-augenbraue">Fragen und Antworten</p>
          <h2>Was Betriebe vor dem Start wissen wollen.</h2>
          {/*
            Acht gleich grosse Kästchen nebeneinander sind keine Antwortliste,
            sondern eine Wand: alle Fragen sind gleich laut, und man liest
            keine. Aufgeklappt ist immer genau eine – die Frage bleibt lesbar,
            die Antwort bekommt Platz.
          */}
          <div className="rp-fragen">
            {FRAGEN.map((f) => {
              const offen = offeneFrage === f.frage
              return (
                <div className={offen ? 'rp-frage offen' : 'rp-frage'} key={f.frage}>
                  <button
                    type="button"
                    className="rp-frage-kopf"
                    aria-expanded={offen}
                    onClick={() => setOffeneFrage(offen ? null : f.frage)}
                  >
                    <span>{f.frage}</span>
                    <span className="rp-frage-zeichen" aria-hidden="true" />
                  </button>
                  {offen && <p className="rp-frage-text">{f.antwort}</p>}
                </div>
              )
            })}
          </div>
        </div>
      </section>

      <section className="rp-schluss">
        <div className="rp-mitte">
          <h2>Noch eine Frage offen?</h2>
          <p>
            Dann fehlt sie auf dieser Seite, und das ist unser Fehler. Schreib uns, und die Antwort
            steht beim nächsten Mal hier.
          </p>
          <button
            className="rp-btn rp-btn-primary rp-btn-gross"
            id="btn-preis-schluss"
            onClick={() => bestellen('pro')}
          >
            {angemeldet ? wort : `${TESTPHASE_TAGE} Tage testen`}
            <span className="rp-pfeil" aria-hidden="true">
              →
            </span>
          </button>
        </div>
      </section>

      <Fuss aufPfad={aufPfad} />
    </div>
  )
}
